/**
 * ============================================================================
 * VEEZE header behaviour
 * ============================================================================
 *
 * Deliberately dependency-free (no `@theme/*` imports) so the header keeps
 * working independently of Horizon's own header modules, which this theme no
 * longer loads. Responsibilities:
 *
 *   1. Publish `--header-height` / `--header-group-height` on `<body>`. Other
 *      Horizon sections (hero, sticky product info, cart summary) size
 *      themselves against these, and `assets/header.js` — which normally keeps
 *      them fresh — is not loaded any more.
 *   2. Sticky modes: `always` and `scroll-up` (retract down, reveal up).
 *   3. Mobile drawer: open/close, focus trap, scroll lock, submenu accordions.
 *   4. Desktop dropdowns: pointer + keyboard, closed on Escape / outside click.
 *   5. Announcement rotation between multiple messages.
 *
 * Re-initialises on `shopify:section:load` so the theme editor stays live.
 */

const SELECTORS = {
  header: '.veeze-header',
  headerGroup: '#header-group',
  inner: '.veeze-header__inner',
  drawer: '[data-veeze-drawer]',
  drawerOpen: '[data-veeze-drawer-open]',
  drawerClose: '[data-veeze-drawer-close]',
  drawerSummary: '[data-veeze-drawer-summary]',
  dropdownItem: '[data-veeze-dropdown]',
  announcement: '[data-veeze-announcement-rotator]',
};

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ==========================================================================
   1. Header height custom properties
   ========================================================================== */

/**
 * Mirrors the measurement contract in `layout/theme.liquid` and
 * `assets/utilities.js` so downstream sections keep their offsets.
 */
class HeaderMetrics {
  constructor(header) {
    this.header = header;
    this.group = document.querySelector(SELECTORS.headerGroup);

    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.header);
    if (this.group) this.observer.observe(this.group);

    // The visible slice of the announcement bar changes as the page scrolls.
    window.addEventListener('scroll', this.updateDrawerTop, { passive: true });

    this.update();
  }

  update = () => {
    const headerHeight = Math.round(this.header.getBoundingClientRect().height);
    document.body.style.setProperty('--header-height', `${headerHeight}px`);

    let groupHeight = 0;
    for (const child of this.group?.children ?? []) {
      if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${Math.round(groupHeight) || headerHeight}px`);

    /*
     * When the announcement bar is also sticky the header has to sit below it,
     * so its own sticky offset is the announcement's measured height.
     *
     * Written to `<body>`: `position: sticky` sits on the *section* element,
     * which is an ancestor of `.veeze-header` — custom properties inherit
     * downward only, so setting it on the header would be invisible there.
     */
    const stickyAnnouncement = document.querySelector('.veeze-announcement[data-sticky="true"]');
    const offset = stickyAnnouncement ? Math.round(stickyAnnouncement.getBoundingClientRect().height) : 0;
    document.body.style.setProperty('--veeze-sticky-top', `${offset}px`);

    this.updateDrawerTop();
  };

  /**
   * How much of the announcement bar is actually on screen.
   *
   * The mobile menu starts below it so the bar stays visible, as in the design.
   * Using the visible height rather than the full height means a scrolled page
   * gets 0 — otherwise the overlay would float below a bar that is no longer
   * there and show a sliver of the page through the gap.
   */
  updateDrawerTop = () => {
    const announcement = document.querySelector('.veeze-announcement');
    let visible = 0;

    if (announcement) {
      const rect = announcement.getBoundingClientRect();
      visible = Math.max(0, Math.min(rect.bottom, rect.height));
    }

    document.body.style.setProperty('--veeze-drawer-top', `${Math.round(visible)}px`);

    /*
     * The bar's FULL height, separate from the visible slice above. The sticky
     * rule offsets #header-group by this when the bar is set to scroll away, so
     * the header still comes to rest flush against the viewport top.
     */
    const full = announcement ? Math.round(announcement.getBoundingClientRect().height) : 0;
    document.body.style.setProperty('--veeze-announcement-full-height', `${full}px`);
  };

  destroy() {
    this.observer.disconnect();
    window.removeEventListener('scroll', this.updateDrawerTop);
  }
}

/* ==========================================================================
   2. Sticky scroll-up mode
   ========================================================================== */

class StickyHeader {
  /** Ignore sub-pixel scroll jitter and the iOS rubber-band zone. */
  static THRESHOLD = 8;

  constructor(header) {
    this.header = header;
    this.lastY = window.scrollY;
    this.ticking = false;

    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;

    window.requestAnimationFrame(() => {
      this.ticking = false;
      const y = Math.max(window.scrollY, 0);
      const delta = y - this.lastY;

      if (Math.abs(delta) < StickyHeader.THRESHOLD) return;

      // Never hide while a menu is open — the trigger would vanish under the cursor.
      const menuOpen =
        this.header.querySelector('.veeze-nav__item[data-open]') != null ||
        document.querySelector(`${SELECTORS.drawer}[data-open]`) != null;

      const shouldHide = delta > 0 && y > this.header.offsetHeight && !menuOpen;
      this.header.dataset.stickyState = shouldHide ? 'hidden' : 'visible';
      this.lastY = y;
    });
  };

  destroy() {
    window.removeEventListener('scroll', this.onScroll);
  }
}

/* ==========================================================================
   3. Drawer
   ========================================================================== */

class Drawer {
  constructor(drawer, header) {
    this.drawer = drawer;
    this.header = header;
    this.trigger = null;

    // One AbortController tears every listener down, so re-initialising in the
    // theme editor can never leave a second set of handlers bound.
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.header.querySelectorAll(SELECTORS.drawerOpen).forEach((button) => {
      button.addEventListener('click', this.open, { signal });
    });

    this.drawer.querySelectorAll(SELECTORS.drawerClose).forEach((button) => {
      button.addEventListener('click', this.close, { signal });
    });

    this.drawer.querySelectorAll(SELECTORS.drawerSummary).forEach((summary) => {
      summary.addEventListener('click', this.toggleSubmenu, { signal });
    });

    this.drawer.addEventListener('click', this.onLinkClick, { signal });

    this.drawer.addEventListener('keydown', this.onKeydown, { signal });

    // Closing the drawer when the viewport grows past the drawer breakpoint
    // prevents a hidden-but-open drawer trapping focus on desktop.
    this.toggleButton = this.header.querySelector('.veeze-header__toggle');
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isOpen || !this.toggleButton) return;
      if (getComputedStyle(this.toggleButton).display === 'none') this.close();
    });
    this.resizeObserver.observe(document.documentElement);
  }

  get isOpen() {
    return this.drawer.hasAttribute('data-open');
  }

  open = (event) => {
    this.trigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.drawer.setAttribute('data-open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');

    document.body.style.overflow = 'hidden';

    /*
     * Focus lands after the slide-in so the browser cannot scroll the panel
     * mid-transition. The timeout is a fallback for the cases where
     * `transitionend` never fires (reduced motion, interrupted transition).
     */
    const focusTarget = this.drawer.querySelector(SELECTORS.drawerClose) ?? this.drawer.querySelector(FOCUSABLE);
    const panel = this.drawer.querySelector('.veeze-drawer__panel');

    if (prefersReducedMotion() || !panel) {
      focusTarget?.focus();
      return;
    }

    let focused = false;
    const focusOnce = () => {
      if (focused) return;
      focused = true;
      focusTarget?.focus();
    };

    panel.addEventListener('transitionend', focusOnce, { once: true });
    setTimeout(focusOnce, 400);
  };

  close = () => {
    if (!this.isOpen) return;

    this.drawer.removeAttribute('data-open');
    document.body.style.overflow = '';

    this.header.querySelectorAll(SELECTORS.drawerOpen).forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    this.trigger?.focus();
    this.trigger = null;
  };

  /*
   * Following a link has to close the drawer.
   *
   * For a link to another page this is invisible — the drawer leaves with the
   * document — which is why nothing needed it until now. For a link to a
   * fragment of the page already open it is the difference between working and
   * not: the hash changes, no navigation happens, and `open()`'s
   * `body { overflow: hidden }` is still in force, so the page cannot scroll to
   * the target and the panel stays on top of it. Nothing appears to happen at
   * all. The "Abo-Boxen" item pointing at /#abo-boxen is exactly that link.
   *
   * Closing before the default action means the scroll lock is already gone by
   * the time the browser scrolls. `trigger` is cleared first so `close()` does
   * not pull focus back to the hamburger and fight the jump.
   */
  onLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.drawer.contains(link)) return;

    // A parent item whose summary is itself a link opens its submenu instead.
    if (link.closest(SELECTORS.drawerSummary)) return;

    this.trigger = null;
    this.close();
  };

  toggleSubmenu = (event) => {
    const summary = event.currentTarget;
    const item = summary.closest('.veeze-drawer__item');
    if (!item) return;

    const open = item.hasAttribute('data-open');
    item.toggleAttribute('data-open', !open);
    summary.setAttribute('aria-expanded', String(!open));
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = [...this.drawer.querySelectorAll(FOCUSABLE)].filter(
      (element) => element.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  destroy() {
    this.controller.abort();
    this.resizeObserver.disconnect();
    document.body.style.overflow = '';
  }
}

/* ==========================================================================
   4. Desktop dropdowns
   ========================================================================== */

class Dropdowns {
  /** Grace period so a diagonal cursor path to the panel does not close it. */
  static CLOSE_DELAY = 120;

  constructor(header) {
    this.header = header;
    this.items = [...header.querySelectorAll(SELECTORS.dropdownItem)];
    this.timers = new WeakMap();

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.items.forEach((item) => {
      const trigger = item.querySelector('.veeze-nav__link');

      item.addEventListener('pointerenter', () => this.open(item), { signal });
      item.addEventListener('pointerleave', () => this.scheduleClose(item), { signal });

      // Focus opens the panel so its links are reachable by keyboard.
      item.addEventListener('focusin', () => this.open(item), { signal });
      item.addEventListener(
        'focusout',
        (event) => {
          if (!item.contains(event.relatedTarget)) this.close(item);
        },
        { signal }
      );

      /*
       * The parent is a link, so a click normally navigates — correct for mouse
       * and keyboard users, who already have hover and focus to reveal the
       * panel. On a touch device there is no hover, so the first tap opens the
       * panel instead and a second tap follows the link.
       */
      trigger?.addEventListener(
        'click',
        (event) => {
          const isTouch = window.matchMedia('(hover: none)').matches;
          if (!isTouch || item.hasAttribute('data-open')) return;

          event.preventDefault();
          this.open(item);
        },
        { signal }
      );
    });

    document.addEventListener('keydown', this.onKeydown, { signal });
    document.addEventListener('pointerdown', this.onPointerDown, { signal });
  }

  open(item) {
    clearTimeout(this.timers.get(item));
    this.items.forEach((other) => other !== item && this.close(other));

    item.setAttribute('data-open', '');
    item.querySelector('.veeze-nav__link')?.setAttribute('aria-expanded', 'true');
  }

  close(item) {
    clearTimeout(this.timers.get(item));
    item.removeAttribute('data-open');
    item.querySelector('.veeze-nav__link')?.setAttribute('aria-expanded', 'false');
  }

  scheduleClose(item) {
    clearTimeout(this.timers.get(item));
    this.timers.set(item, setTimeout(() => this.close(item), Dropdowns.CLOSE_DELAY));
  }

  closeAll() {
    this.items.forEach((item) => this.close(item));
  }

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const open = this.items.find((item) => item.hasAttribute('data-open'));
    if (!open) return;

    this.close(open);
    open.querySelector('.veeze-nav__link')?.focus();
  };

  onPointerDown = (event) => {
    if (!this.header.contains(event.target)) this.closeAll();
  };

  destroy() {
    this.items.forEach((item) => clearTimeout(this.timers.get(item)));
    this.controller.abort();
  }
}

/* ==========================================================================
   5. Announcement rotation
   ========================================================================== */

class AnnouncementRotator {
  constructor(rotator) {
    this.rotator = rotator;
    this.items = [...rotator.querySelectorAll('.veeze-announcement__item')];
    this.index = 0;
    this.paused = false;

    if (this.items.length < 2) return;

    this.interval = Number(rotator.dataset.veezeAnnouncementInterval || 5) * 1000;
    rotator.setAttribute('data-rotating', '');

    // Pausing on hover and on focus keeps links inside a message reachable.
    rotator.addEventListener('pointerenter', () => (this.paused = true));
    rotator.addEventListener('pointerleave', () => (this.paused = false));
    rotator.addEventListener('focusin', () => (this.paused = true));
    rotator.addEventListener('focusout', () => (this.paused = false));

    this.timer = setInterval(this.advance, this.interval);
  }

  advance = () => {
    if (this.paused || document.hidden) return;

    this.items[this.index]?.removeAttribute('data-active');
    this.items[this.index]?.setAttribute('aria-hidden', 'true');

    this.index = (this.index + 1) % this.items.length;

    this.items[this.index]?.setAttribute('data-active', '');
    this.items[this.index]?.removeAttribute('aria-hidden');
  };

  destroy() {
    clearInterval(this.timer);
  }
}

/* ==========================================================================
   Bootstrap
   ========================================================================== */

/** @type {Array<{destroy: () => void}>} */
let instances = [];

function init() {
  instances.forEach((instance) => instance.destroy());
  instances = [];

  const header = document.querySelector(SELECTORS.header);

  if (header) {
    instances.push(new HeaderMetrics(header));
    instances.push(new Dropdowns(header));

    if (header.dataset.sticky === 'scroll-up') {
      instances.push(new StickyHeader(header));
    }

    const drawer = document.querySelector(SELECTORS.drawer);
    if (drawer) instances.push(new Drawer(drawer, header));
  }

  document.querySelectorAll(SELECTORS.announcement).forEach((rotator) => {
    instances.push(new AnnouncementRotator(rotator));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Theme editor: re-bind after a header or announcement section is re-rendered.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement && event.target.querySelector('.veeze-header, .veeze-announcement')) {
    init();
  }
});
